/**
 * Fixed-capacity ring buffer. Every temporal feature in the recogniser reads
 * from one of these, so it must allocate nothing per frame.
 * `at(0)` is the newest sample, `at(1)` the one before it, and so on.
 */
export class Ring<T> {
  private buf: (T | undefined)[];
  private head = 0;
  private _size = 0;

  constructor(readonly capacity: number) {
    this.buf = new Array(capacity);
  }

  get size(): number {
    return this._size;
  }
  get full(): boolean {
    return this._size === this.capacity;
  }

  push(v: T): void {
    this.buf[this.head] = v;
    this.head = (this.head + 1) % this.capacity;
    if (this._size < this.capacity) this._size++;
  }

  /** `i` counts backwards from the newest sample. Returns undefined if out of range. */
  at(i: number): T | undefined {
    if (i < 0 || i >= this._size) return undefined;
    return this.buf[(this.head - 1 - i + this.capacity * 2) % this.capacity];
  }

  get last(): T | undefined {
    return this.at(0);
  }

  clear(): void {
    this.head = 0;
    this._size = 0;
    this.buf.fill(undefined);
  }

  /** Newest-first iteration over up to `n` samples. */
  *recent(n = this._size): Generator<T> {
    const k = Math.min(n, this._size);
    for (let i = 0; i < k; i++) yield this.at(i) as T;
  }

  toArray(): T[] {
    const out: T[] = [];
    for (let i = this._size - 1; i >= 0; i--) out.push(this.at(i) as T);
    return out;
  }
}
